import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { View, Text, Pressable, ActivityIndicator } from '../lib/react-native-compat';
import { saveQuantitySchedule } from '../db/company';
import {
    seedQuantitySchedule, suggestActivities, ownershipMap, datesFromActivities,
    scheduleQuantity, nameScore, activityKey,
} from './quantityMatch';
import { workingDayCount } from './scheduleCalendar';

// Mapping the schedule to the budget.
//
// The budget knows quantities per JDE phase code. The schedule knows dates. On
// most jobs nothing connects them — Block F had zero of 117 activities carrying
// a phase — so the connection is guessed from activity names and then has to be
// confirmed by somebody who knows the job. That confirmation is this panel.
//
// What it adds over a plain grid:
//   · every row says how it was matched — by phase code (certain) or by name
//     (a guess), so a PM knows which ones to actually look at;
//   · the activity picker lets a row be re-bound to whatever activities really
//     drive it, and the dates re-derive from the new set;
//   · quantities show both sides. A schedule that carries loaded quantities
//     wins over the budget, but the PM can push it back per row.
//
// The bindings are saved, so re-uploading a revised schedule re-drives every
// date instead of being re-keyed.

const CURVES = [
    ['linear', 'Even'],
    ['frontEnd', 'Front-loaded'],
    ['backEnd', 'Back-loaded'],
    ['bell', 'Ramp up/down'],
];

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const fmt = (v, dp = 0) => (Number.isFinite(Number(v))
    ? Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : '—');

const shortDate = (d) => (d ? moment(d).format('M/D/YY') : '—');

const card = {
    borderRadius: 10,
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    padding: 12,
    marginBottom: 12,
};

const cell = {
    fontSize: 12,
    padding: '4px 6px',
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    color: '#0F172A',
};

const th = {
    fontSize: 10,
    fontWeight: '700',
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    padding: '6px 6px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

const td = { padding: '3px 6px', verticalAlign: 'middle', borderTop: '1px solid #F1F5F9' };

const btn = (primary, disabled) => ({
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 14,
    paddingRight: 14,
    borderRadius: 8,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    backgroundColor: primary ? '#1D4ED8' : '#fff',
    border: `1px solid ${primary ? '#1D4ED8' : '#CBD5E1'}`,
});

const btnText = (primary) => ({ fontSize: 12, fontWeight: '700', color: primary ? '#fff' : '#334155' });

/** What still needs a human before this row produces a curve. */
export const gapOf = (r) => {
    if (!num(r.qty)) return 'needs quantity';
    if (!r.startDate) return 'needs start';
    if (!r.finishDate && !num(r.dailyRate)) return 'needs finish';
    if (r.finishDate && r.startDate && moment(r.finishDate).isBefore(moment(r.startDate))) return 'finish before start';
    return null;
};

const nameOf = (a) => a?.Activity_Name || a?.activityName || a?.Activity_Code || '';

/** The activities a row is bound to, resolved out of the saved key list. */
const boundActivities = (row, byKey) => (row.boundKeys || [])
    .map((k) => byKey.get(k))
    .filter(Boolean);

/**
 * Seeded rows name their bound activities by activity CODE — that is what
 * survives a re-upload — while the picker works in activity keys. Bridge them.
 */
const withKeys = (acts) => (r) => {
    const codes = String(r.activityCode ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    const matched = acts.filter((a) => codes.includes(String(a.Activity_Code ?? a.activityCode ?? '')));
    return { ...r, boundKeys: matched.map(activityKey) };
};

/**
 * One row re-bound to a new set of activities: dates and the schedule-side
 * quantity both follow the binding. Shared by the table's checkboxes and by the
 * Gantt gutter's phase dropdown, so the two edit the same thing the same way.
 */
const rebound = (r, nextKeys, byKey) => {
    const bound = nextKeys.map((k) => byKey.get(k)).filter(Boolean);
    const { startDate, finishDate } = datesFromActivities(bound);
    const scheduled = scheduleQuantity(bound);
    return {
        ...r,
        boundKeys: nextKeys,
        matchedBy: nextKeys.length > 0 ? 'manual' : null,
        // Dates follow the binding; a row with nothing bound keeps whatever was
        // keyed by hand rather than being blanked.
        startDate: startDate || r.startDate,
        finishDate: finishDate || r.finishDate,
        scheduleQty: scheduled.qty || null,
        scheduleUom: scheduled.uom || null,
        // Only follow the schedule's quantity if this row wasn't already pinned
        // to the budget by hand.
        ...(r.qtySource === 'schedule' && scheduled.qty
            ? { qty: scheduled.qty, uom: scheduled.uom || r.uom }
            : {}),
    };
};

// ── activity picker ─────────────────────────────────────────────────────────

const ActivityPicker = ({ row, activities, onToggle, onClose }) => {
    const [q, setQ] = useState('');
    const bound = new Set(row.boundKeys || []);

    // Bound first so what is already picked never scrolls out of reach, then by
    // how well each candidate matches the cost code's description.
    const ranked = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return activities
            .map((a) => ({ a, key: activityKey(a), score: nameScore(row.description, nameOf(a)) }))
            .filter(({ a, key }) => {
                if (!needle) return true;
                if (bound.has(key)) return true;
                return `${nameOf(a)} ${a.Activity_Code || ''} ${a.WBS_Path || ''}`.toLowerCase().includes(needle);
            })
            .sort((x, y) => (bound.has(y.key) ? 1 : 0) - (bound.has(x.key) ? 1 : 0)
                || y.score - x.score
                || nameOf(x.a).localeCompare(nameOf(y.a)))
            .slice(0, 300);
    }, [activities, q, row.description, row.boundKeys]);

    return (
        <View style={{
            border: '1px solid #CBD5E1', borderRadius: 8, backgroundColor: '#F8FAFC', padding: 10, marginTop: 6,
        }}
        >
            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 8, marginBottom: 8 }}>
                <Text style={{ fontSize: 12, fontWeight: '700', color: '#0F172A' }}>
                    {`Activities driving ${row.costCode}`}
                </Text>
                <Text style={{ fontSize: 11, color: '#64748B', flex: 1 }} numberOfLines={1}>
                    {row.description}
                </Text>
                <input
                    type="text"
                    value={q}
                    placeholder="Search activities…"
                    onChange={(e) => setQ(e.target.value)}
                    style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #CBD5E1', borderRadius: 6, minWidth: 200 }}
                />
                <Pressable onPress={onClose} style={btn(false, false)}>
                    <Text style={btnText(false)}>Done</Text>
                </Pressable>
            </View>

            {activities.length === 0 ? (
                <Text style={{ fontSize: 12, color: '#94A3B8' }}>
                    No schedule uploaded — there is nothing to bind these cost codes to yet.
                </Text>
            ) : (
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {ranked.map(({ a, key, score }) => {
                        const on = bound.has(key);
                        return (
                            <label
                                key={key}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '4px 6px',
                                    borderTop: '1px solid #E2E8F0',
                                    backgroundColor: on ? '#EFF6FF' : 'transparent',
                                    cursor: 'pointer',
                                }}
                            >
                                <input type="checkbox" checked={on} onChange={() => onToggle(key)} />
                                <span style={{ fontSize: 12, color: '#0F172A', flex: 1, minWidth: 0 }}>
                                    {a.Cost_Code ? (
                                        <span style={{ color: '#1D4ED8', fontWeight: 700 }}>{`${a.Cost_Code} `}</span>
                                    ) : null}
                                    {nameOf(a)}
                                    {a.WBS_Path ? (
                                        <span style={{ color: '#94A3B8' }}>{`  ·  ${a.WBS_Path}`}</span>
                                    ) : null}
                                </span>
                                <span style={{ fontSize: 10.5, color: '#94A3B8', whiteSpace: 'nowrap' }}>
                                    {`${shortDate(a.Start_Date)} – ${shortDate(a.Finish_Date)}`}
                                </span>
                                <span style={{ fontSize: 10.5, color: score >= 0.45 ? '#166534' : '#CBD5E1', width: 30, textAlign: 'right' }}>
                                    {score > 0 ? score.toFixed(2) : ''}
                                </span>
                            </label>
                        );
                    })}
                </div>
            )}
        </View>
    );
};

// ── panel ───────────────────────────────────────────────────────────────────

const ScheduleMapping = ({
    buId, baseline, budgetPhases, activities, quantity, userName, loading, disciplineMap,
    onSaved, onRowsChange, controllerRef, forceOpen, onClose,
}) => {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [dirty, setDirty] = useState(false);
    const [openRow, setOpenRow] = useState(null);          // cost code being re-bound
    const [showAll, setShowAll] = useState(true);
    // Collapsed by default — it is a wide grid with a row per cost code, and on
    // a real job that is fifty rows the PM did not ask to see yet. The header
    // carries the counts, which is what they came for. In the review modal the
    // grid IS the point, so forceOpen holds it open and hides the toggle.
    const [open, setOpen] = useState(false);
    const expanded = forceOpen || open;

    const acts = useMemo(() => activities || [], [activities]);
    const byKey = useMemo(() => new Map(acts.map((a) => [activityKey(a), a])), [acts]);

    // Cost-code rows of each source; the time-phased rows share the tables.
    // The baseline workbook if there is one, else the job's own budget phases —
    // a schedule can be mapped before anybody produces the workbook.
    const baseRows = useMemo(() => {
        const uploaded = (baseline || []).filter((r) => !r.Period_Date);
        return uploaded.length > 0 ? uploaded : (budgetPhases || []);
    }, [baseline, budgetPhases]);
    const fromBudget = (baseline || []).filter((r) => !r.Period_Date).length === 0;
    const savedRows = useMemo(() => (quantity || []).filter((r) => !r.Period_Date), [quantity]);

    /** Re-attach the bound-activity detail a saved row only carries as codes. */
    const hydrate = useCallback((r, i) => {
        const codes = String(r.Activity_Code ?? '').split(',').map((s) => s.trim()).filter(Boolean);
        // Saved rows store activity CODES; the picker works in activity keys.
        const matched = acts.filter((a) => codes.includes(String(a.Activity_Code ?? '')));
        const scheduled = scheduleQuantity(matched);
        return {
            costCode: String(r.Cost_Code ?? ''),
            description: r.Description ?? '',
            uom: r.UOM ?? '',
            qty: num(r.Budget_Qty),
            qtySource: r.Qty_Source || 'budget',
            budgetQty: num(r.Budget_Qty),
            scheduleQty: scheduled.qty || null,
            scheduleUom: scheduled.uom || null,
            budgetHours: r.Budget_Hours == null ? null : num(r.Budget_Hours),
            // SQL DATE columns serialize as UTC midnights — read them as UTC or
            // the Central-time local parse walks every date back a day.
            startDate: r.Start_Date ? moment.utc(r.Start_Date).format('YYYY-MM-DD') : null,
            finishDate: r.Finish_Date ? moment.utc(r.Finish_Date).format('YYYY-MM-DD') : null,
            dailyRate: r.Daily_Rate == null ? null : num(r.Daily_Rate),
            curve: r.Curve || 'linear',
            workDays: r.Work_Days ? Number(r.Work_Days) : 6,
            boundKeys: matched.map(activityKey),
            matchedBy: matched.length > 0 ? 'saved' : null,
            sortOrder: i + 1,
        };
    }, [acts]);

    // A saved mapping is the source of truth once it exists; seeding is only for
    // the first time through, so reopening the tab never silently discards what
    // somebody already set up.
    useEffect(() => {
        if (dirty) return;
        if (savedRows.length > 0) { setRows(savedRows.map(hydrate)); return; }
        if (baseRows.length > 0) { setRows(seedQuantitySchedule(baseRows, acts).map(withKeys(acts))); return; }
        setRows([]);
    }, [savedRows, baseRows, acts, hydrate, dirty]);

    // The validator upstairs measures the same rows this panel edits, so it sees
    // an unsaved re-bind immediately rather than waiting for a round trip.
    useEffect(() => { if (onRowsChange) onRowsChange(rows); }, [rows, onRowsChange]);

    const patch = (code, changes) => {
        setRows((prev) => prev.map((r) => (r.costCode === code ? { ...r, ...changes } : r)));
        setDirty(true);
        setNotice('');
    };

    /** Re-bind one row and re-derive its dates and schedule quantity. */
    const toggleBinding = (code, key) => {
        setRows((prev) => prev.map((r) => {
            if (r.costCode !== code) return r;
            const keys = new Set(r.boundKeys || []);
            if (keys.has(key)) keys.delete(key); else keys.add(key);
            return rebound(r, [...keys], byKey);
        }));
        setDirty(true);
        setNotice('');
    };

    // The Gantt's gutter edits this panel's rows rather than keeping a second
    // copy: one mapping, one Save button, no way for the two to disagree.
    useEffect(() => {
        if (!controllerRef) return undefined;
        controllerRef.current = {
            // An activity belongs to exactly one cost code, so assigning it
            // takes it off whatever row held it.
            assignActivity: (key, costCode) => {
                setRows((prev) => prev.map((r) => {
                    const had = (r.boundKeys || []).includes(key);
                    if (r.costCode === costCode) {
                        return had ? r : rebound(r, [...(r.boundKeys || []), key], byKey);
                    }
                    return had ? rebound(r, (r.boundKeys || []).filter((k) => k !== key), byKey) : r;
                }));
                setDirty(true);
                setNotice('');
            },
            setQty: (costCode, qty) => {
                setRows((prev) => prev.map((r) => (r.costCode === costCode
                    // Typing a quantity pins the row to that number, so the
                    // schedule's own quantity can't quietly overwrite it.
                    ? { ...r, qty, budgetQty: qty, qtySource: 'budget' }
                    : r)));
                setDirty(true);
                setNotice('');
            },
        };
        return () => { controllerRef.current = null; };
    }, [controllerRef, byKey]);

    /** Swap which side this row's quantity comes from. */
    const toggleQtySource = (code) => {
        setRows((prev) => prev.map((r) => {
            if (r.costCode !== code) return r;
            const next = r.qtySource === 'schedule' ? 'budget' : 'schedule';
            if (next === 'schedule' && !r.scheduleQty) return r;    // nothing to switch to
            return {
                ...r,
                qtySource: next,
                qty: next === 'schedule' ? r.scheduleQty : r.budgetQty,
                uom: (next === 'schedule' ? r.scheduleUom : null) || r.uom,
            };
        }));
        setDirty(true);
    };

    const autoMap = useCallback(() => {
        if (baseRows.length === 0) {
            setError('Upload the productivity baseline first — that is where the cost codes come from.');
            return;
        }
        setError('');
        setRows(seedQuantitySchedule(baseRows, acts).map(withKeys(acts)));
        setDirty(true);
        setNotice('Auto-mapped from the baseline and the active schedule. Check the guessed bindings before saving.');
    }, [baseRows, acts]);

    // Re-run the date binding against whatever schedule is active now. This is
    // the payoff for storing the bindings: a re-uploaded schedule updates every
    // row's dates without anybody re-keying them.
    const redriveDates = useCallback(() => {
        if (acts.length === 0) { setError('No schedule activities loaded to re-drive dates from.'); return; }
        setError('');
        const owner = ownershipMap(rows, acts);
        let touched = 0;
        setRows((prev) => prev.map((r) => {
            // A hand-picked binding is a decision, not a guess — re-drive its
            // dates from the activities it names, but never re-pick them.
            const bound = r.matchedBy === 'manual'
                ? boundActivities(r, byKey)
                : suggestActivities(r, acts, owner).map((s) => s.activity);
            if (bound.length === 0) return r;
            const { startDate, finishDate } = datesFromActivities(bound);
            if (!startDate && !finishDate) return r;
            touched += 1;
            return {
                ...r,
                startDate: startDate || r.startDate,
                finishDate: finishDate || r.finishDate,
                boundKeys: bound.map(activityKey),
            };
        }));
        setDirty(true);
        setNotice(`Re-drove dates for ${touched} cost code${touched === 1 ? '' : 's'} from the active schedule.`);
    }, [acts, rows, byKey]);

    const save = useCallback(async () => {
        if (rows.length === 0) return;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const res = await saveQuantitySchedule({
                buId: String(buId),
                savedBy: userName || null,
                source: 'mapping',
                notes: 'Schedule → budget mapping',
                rows: rows.map((r) => ({
                    costCode: r.costCode,
                    description: r.description,
                    uom: r.uom,
                    qty: num(r.qty),
                    budgetHours: r.budgetHours,
                    startDate: r.startDate,
                    finishDate: r.finishDate,
                    dailyRate: r.dailyRate ? num(r.dailyRate) : null,
                    curve: r.curve,
                    workDays: r.workDays,
                    qtySource: r.qtySource,
                    // Saved as codes so a re-upload with new internal ids still
                    // finds them; the picker rehydrates keys from these.
                    activityCode: boundActivities(r, byKey)
                        .map((a) => a.Activity_Code || a.activityCode)
                        .filter(Boolean).join(', ') || null,
                })),
            });
            if (res?.success === false || res?.error) throw new Error(res.error || 'Save failed');
            setDirty(false);
            setNotice(`Saved — ${fmt(res?.codeCount)} cost codes, ${fmt(res?.periodCount)} planned days.`);
            if (onSaved) onSaved();
        } catch (e) {
            setError(e?.message || 'Could not save the mapping.');
        } finally {
            setBusy(false);
        }
    }, [rows, buId, userName, byKey, onSaved]);

    const unbound = useMemo(() => rows.filter((r) => (r.boundKeys || []).length === 0), [rows]);
    const guessed = useMemo(() => rows.filter((r) => r.matchedBy === 'name'), [rows]);

    const tagged = useMemo(() => acts.filter((a) => a.Cost_Code).length, [acts]);
    const withQty = useMemo(() => acts.filter((a) => num(a.Budget_Qty) > 0).length, [acts]);

    // Unbound rows first — they are the gap the validator names, and the fix is
    // right here, so making somebody scroll for them would be perverse.
    const ordered = useMemo(() => {
        const rank = (r) => ((r.boundKeys || []).length === 0 ? 0 : (r.matchedBy === 'name' ? 1 : 2));
        return [...rows].sort((a, b) => rank(a) - rank(b) || a.costCode.localeCompare(b.costCode));
    }, [rows]);

    const shown = showAll ? ordered : ordered.filter((r) => (r.boundKeys || []).length === 0 || r.matchedBy === 'name');

    if (!buId) return null;

    return (
        // In the modal the panel IS the sheet — a bordered card inside a
        // bordered dialog is one box too many.
        <View style={forceOpen
            ? { ...card, border: 'none', borderRadius: 12, marginBottom: 0, padding: 20 }
            : card}
        >
            <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
                // 49 cost codes is a long scroll; Save shouldn't scroll away.
                ...(forceOpen
                    ? {
                        position: 'sticky',
                        top: 0,
                        zIndex: 5,
                        backgroundColor: '#fff',
                        // Span the sheet's full width so rows don't show
                        // through the card's side padding as they scroll under.
                        marginLeft: -20,
                        marginRight: -20,
                        marginTop: -20,
                        paddingLeft: 20,
                        paddingRight: 20,
                        paddingTop: 20,
                        paddingBottom: 12,
                        marginBottom: 4,
                        borderBottom: '1px solid #F1F5F9',
                    }
                    : {}),
            }}
            >
                <Pressable
                    onPress={forceOpen ? undefined : () => setOpen((v) => !v)}
                    style={{ cursor: forceOpen ? 'default' : 'pointer' }}
                >
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A' }}>
                        {forceOpen ? 'Review the mapping' : `${open ? '▾' : '▸'}  Schedule → budget mapping`}
                    </Text>
                    <Text style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                        {rows.length === 0
                            ? 'Nothing mapped yet — auto-map to bind the budget’s cost codes to the schedule.'
                            : `${rows.length} cost codes · ${unbound.length} unbound · ${guessed.length} matched by name`}
                    </Text>
                </Pressable>
                <View style={{ flex: 1 }} />
                {(busy || loading) && <ActivityIndicator size="small" />}
                <Pressable onPress={autoMap} disabled={busy} style={btn(false, busy)}>
                    <Text style={btnText(false)}>{rows.length ? 'Re-run auto-map' : 'Auto-map'}</Text>
                </Pressable>
                <Pressable onPress={redriveDates} disabled={busy || rows.length === 0} style={btn(false, busy || rows.length === 0)}>
                    <Text style={btnText(false)}>Re-drive dates</Text>
                </Pressable>
                <Pressable onPress={save} disabled={busy || rows.length === 0 || !dirty} style={btn(true, busy || rows.length === 0 || !dirty)}>
                    <Text style={btnText(true)}>{!dirty && savedRows.length > 0 ? 'Saved' : 'Save mapping'}</Text>
                </Pressable>
                {onClose ? (
                    <Pressable onPress={onClose} disabled={busy} style={btn(false, busy)}>
                        <Text style={btnText(false)}>Close</Text>
                    </Pressable>
                ) : null}
            </View>

            {/* Action feedback stays visible collapsed — an auto-map or a save
                the PM just triggered must report back whether the grid is open. */}
            {error ? <Text style={{ fontSize: 12, color: '#B91C1C', marginTop: 8 }}>{error}</Text> : null}
            {notice ? <Text style={{ fontSize: 12, color: '#166534', marginTop: 8 }}>{notice}</Text> : null}

            {/* 'needs quantity' on every row is not a mystery if we say why. */}
            {rows.length > 0 && fromBudget && (
                <Text style={{ fontSize: 11, color: '#B45309', marginTop: 8 }}>
                    No productivity baseline uploaded — these cost codes come from the job’s budget, so the
                    dates map automatically but the quantities have to be entered here (or upload the baseline).
                </Text>
            )}

            {expanded && acts.length > 0 && (
                <Text style={{ fontSize: 11, color: '#64748B', marginTop: 8 }}>
                    {`This schedule carries a phase code on ${fmt(tagged)} of ${fmt(acts.length)} activities`
                      + ` and a quantity on ${fmt(withQty)}.`
                      + (tagged === 0 ? ' Everything below was matched by name — worth a read.' : '')}
                </Text>
            )}

            {!expanded ? null : rows.length === 0 ? (
                <Text style={{ fontSize: 12, color: '#64748B', marginTop: 10, lineHeight: '18px' }}>
                    Auto-mapping reads the cost codes, quantities and units from the active productivity baseline,
                    then binds each one to the schedule activities that carry its phase code — or, failing that,
                    that share its name. Name matches are guesses; confirm them here before saving.
                </Text>
            ) : (
                <>
                    <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 8, marginTop: 10 }}>
                        <Pressable onPress={() => setShowAll((v) => !v)} style={{ cursor: 'pointer' }}>
                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#1D4ED8' }}>
                                {showAll
                                    ? `Show only what needs attention (${unbound.length + guessed.length})`
                                    : `Show all ${rows.length} cost codes`}
                            </Text>
                        </Pressable>
                    </View>

                    <div style={{
                        overflowX: 'auto',
                        marginTop: 6,
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        // Its own scroller in the modal, so the header row
                        // sticks to the grid rather than fighting the panel's.
                        ...(forceOpen ? { maxHeight: '62vh', overflowY: 'auto' } : {}),
                    }}
                    >
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1180 }}>
                            <thead>
                                {/* The header sticks: thirteen columns is a lot
                                    to hold in your head at row forty. */}
                                <tr style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#F8FAFC', boxShadow: 'inset 0 -1px 0 #E2E8F0' }}>
                                    <th style={th}>Cost code</th>
                                    <th style={th}>Description</th>
                                    <th style={th}>Discipline</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Budget qty</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Schedule qty</th>
                                    <th style={th}>Using</th>
                                    <th style={th}>UOM</th>
                                    <th style={th}>Start</th>
                                    <th style={th}>Finish</th>
                                    <th style={th}>Curve</th>
                                    <th style={th}>Days/wk</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Per day</th>
                                    <th style={th}>Bound activities</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map((r) => {
                                    const gap = gapOf(r);
                                    const bound = boundActivities(r, byKey);
                                    const days = r.finishDate ? workingDayCount(r.startDate, r.finishDate, r.workDays) : 0;
                                    const perDay = days > 0 ? num(r.qty) / days : num(r.dailyRate);
                                    const isOpen = openRow === r.costCode;
                                    return (
                                        <React.Fragment key={r.costCode}>
                                            <tr style={{ backgroundColor: bound.length === 0 ? '#FEF2F2' : (gap ? '#FFFBEB' : 'transparent') }}>
                                                <td style={td}>
                                                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#1D4ED8' }}>{r.costCode}</Text>
                                                </td>
                                                <td style={{ ...td, maxWidth: 180 }}>
                                                    <Text numberOfLines={1} style={{ fontSize: 12, color: '#334155' }}>{r.description}</Text>
                                                </td>
                                                <td style={{ ...td, maxWidth: 120 }}>
                                                    <Text numberOfLines={1} style={{ fontSize: 11, color: '#64748B' }}>
                                                        {(disciplineMap || {})[r.costCode] || '—'}
                                                    </Text>
                                                </td>
                                                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                    <Text style={{ fontSize: 12, color: r.qtySource ==='budget' ? '#0F172A' : '#94A3B8' }}>
                                                        {r.budgetQty ? fmt(r.budgetQty) : '—'}
                                                    </Text>
                                                </td>
                                                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                    <Text style={{ fontSize: 12, color: r.qtySource ==='schedule' ? '#0F172A' : '#94A3B8' }}>
                                                        {r.scheduleQty ? fmt(r.scheduleQty) : '—'}
                                                    </Text>
                                                </td>
                                                <td style={{ ...td, width: 96 }}>
                                                    <Pressable
                                                        onPress={() => toggleQtySource(r.costCode)}
                                                        disabled={!r.scheduleQty}
                                                        title={r.scheduleQty
                                                            ? 'Switch which side this quantity comes from'
                                                            : 'The schedule carries no quantity for this code'}
                                                        style={{
                                                            borderRadius: 6,
                                                            border: '1px solid #CBD5E1',
                                                            paddingTop: 3,
                                                            paddingBottom: 3,
                                                            paddingLeft: 7,
                                                            paddingRight: 7,
                                                            backgroundColor: r.qtySource === 'schedule' ? '#EFF6FF' : '#F8FAFC',
                                                            cursor: r.scheduleQty ? 'pointer' : 'default',
                                                            opacity: r.scheduleQty ? 1 : 0.5,
                                                        }}
                                                    >
                                                        <Text style={{ fontSize: 10.5, fontWeight: '700', color: r.qtySource === 'schedule' ? '#1D4ED8' : '#475569' }}>
                                                            {r.qtySource === 'schedule' ? 'Schedule' : 'Budget'}
                                                        </Text>
                                                    </Pressable>
                                                </td>
                                                <td style={{ ...td, width: 62 }}>
                                                    <input
                                                        type="text"
                                                        value={r.uom ?? ''}
                                                        onChange={(e) => patch(r.costCode, { uom: e.target.value })}
                                                        style={cell}
                                                    />
                                                </td>
                                                <td style={{ ...td, width: 128 }}>
                                                    <input
                                                        type="date"
                                                        value={r.startDate ?? ''}
                                                        onChange={(e) => patch(r.costCode, { startDate: e.target.value || null })}
                                                        style={cell}
                                                    />
                                                </td>
                                                <td style={{ ...td, width: 128 }}>
                                                    <input
                                                        type="date"
                                                        value={r.finishDate ?? ''}
                                                        onChange={(e) => patch(r.costCode, { finishDate: e.target.value || null })}
                                                        style={cell}
                                                    />
                                                </td>
                                                <td style={{ ...td, width: 118 }}>
                                                    <select
                                                        value={r.curve}
                                                        onChange={(e) => patch(r.costCode, { curve: e.target.value })}
                                                        style={cell}
                                                    >
                                                        {CURVES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                                    </select>
                                                </td>
                                                <td style={{ ...td, width: 64 }}>
                                                    <select
                                                        value={r.workDays}
                                                        onChange={(e) => patch(r.costCode, { workDays: Number(e.target.value) })}
                                                        style={cell}
                                                    >
                                                        <option value={5}>5</option>
                                                        <option value={6}>6</option>
                                                        <option value={7}>7</option>
                                                    </select>
                                                </td>
                                                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <Text style={{ fontSize: 12, color: gap ? '#B45309' : '#0F172A', fontWeight: '600' }}>
                                                        {gap || `${fmt(perDay, perDay < 10 ? 1 : 0)} ${r.uom || ''}`}
                                                    </Text>
                                                    {!gap && days > 0 && (
                                                        <Text style={{ fontSize: 10, color: '#94A3B8' }}>{`${days} days`}</Text>
                                                    )}
                                                </td>
                                                <td style={{ ...td, maxWidth: 280 }}>
                                                    <Pressable
                                                        onPress={() => setOpenRow(isOpen ? null : r.costCode)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <Text
                                                            numberOfLines={1}
                                                            title={bound.map(nameOf).join('\n')}
                                                            style={{ fontSize: 11, color: bound.length ? '#475569' : '#B91C1C' }}
                                                        >
                                                            {bound.length
                                                                ? `${[...new Set(bound.map(nameOf))].join(' · ')}`
                                                                : 'not bound — click to pick activities'}
                                                        </Text>
                                                    </Pressable>
                                                    {bound.length > 0 && (
                                                        <Text style={{ fontSize: 10, color: '#94A3B8' }}>
                                                            {[
                                                                `${bound.length} activit${bound.length === 1 ? 'y' : 'ies'}`,
                                                                r.matchedBy === 'code' ? 'by phase code'
                                                                    : r.matchedBy === 'name' ? 'by name — confirm'
                                                                        : r.matchedBy === 'manual' ? 'picked by hand' : 'saved',
                                                            ].join(' · ')}
                                                        </Text>
                                                    )}
                                                </td>
                                            </tr>
                                            {isOpen && (
                                                <tr>
                                                    <td style={{ ...td, padding: 0 }} colSpan={13}>
                                                        <ActivityPicker
                                                            row={r}
                                                            activities={acts}
                                                            onToggle={(key) => toggleBinding(r.costCode, key)}
                                                            onClose={() => setOpenRow(null)}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </View>
    );
};

export default ScheduleMapping;
